import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectsAPI, usersAPI, projectStagesAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Loader2, ArrowLeft, ChevronDown, LayoutDashboard, DollarSign, ClipboardList, CalendarDays, FileText, Activity } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { StageBadge, formatCurrency } from './ProjectsList';
import { ProjectOverview } from '../components/projects/ProjectOverview';
import { ProjectBudget } from '../components/projects/ProjectBudget';
import { ProjectTasks } from '../components/projects/ProjectTasks';
import { ProjectSchedule } from '../components/projects/ProjectSchedule';
import { ProjectDailyLogs } from '../components/projects/ProjectDailyLogs';
import { ProjectFiles } from '../components/projects/ProjectFiles';
import { ProjectActivity } from '../components/projects/ProjectActivity';
import { JobCustomFields } from '../components/JobCustomFields';
import { ApplyWorkflowButton } from '../components/ApplyWorkflowButton';
import { GanttChart } from 'lucide-react';

const TABS = [
  { id: 'overview',   label: 'Overview',   icon: LayoutDashboard },
  { id: 'budget',     label: 'Budget',     icon: DollarSign },
  { id: 'schedule',   label: 'Schedule',   icon: GanttChart },
  { id: 'tasks',      label: 'Tasks',      icon: ClipboardList },
  { id: 'daily-logs', label: 'Daily Logs', icon: CalendarDays },
  { id: 'files',      label: 'Files',      icon: FileText },
  { id: 'activity',   label: 'Activity',   icon: Activity },
];

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isOwner, isOffice } = useAuth();

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [teamUsers, setTeamUsers] = useState([]);
  const [stages, setStages] = useState([]);
  const [stageConfig, setStageConfig] = useState({});

  const fetchProject = useCallback(async () => {
    try {
      const res = await projectsAPI.get(id);
      setProject(res.data);
    } catch {
      toast.error('Failed to load project');
      navigate('/jobs');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchProject(); }, [fetchProject]);

  useEffect(() => {
    usersAPI.list().then(res => {
      setTeamUsers(Array.isArray(res.data) ? res.data.filter(u => u.is_active) : []);
    }).catch(() => {});
    projectStagesAPI.list().then(res => {
      const s = res.data.stages || [];
      setStages(s);
      const cfg = {};
      s.forEach(st => { cfg[st.slug] = { label: st.name, color: st.color, id: st.id }; });
      setStageConfig(cfg);
    }).catch(() => {});
  }, []);

  const handleStageChange = async (newStage) => {
    try {
      await projectsAPI.update(id, { project_stage: newStage });
      setProject(prev => ({ ...prev, project_stage: newStage }));
      const cfg = stageConfig[newStage];
      toast.success(`Stage updated to ${cfg?.label || newStage}`);
    } catch {
      toast.error('Failed to update stage');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  if (!project) return null;

  const canEdit = isOwner || isOffice;

  return (
    <Layout>
      <div className="space-y-0" data-testid="project-detail">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="min-w-0 flex-1">
            <button onClick={() => navigate('/jobs?tab=projects')} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2 transition-colors" data-testid="project-back-btn">
              <ArrowLeft className="w-4 h-4" /> Back to Projects
            </button>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold truncate" data-testid="project-title">{project.job_name}</h1>
              <span className="text-sm text-muted-foreground font-mono">#{project.job_number}</span>
            </div>
            {project.customer?.name && (
              <p className="text-sm text-muted-foreground mt-1">{project.customer.name}</p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {canEdit && (
              <ApplyWorkflowButton jobId={id} jobMode="project" onApplied={fetchProject} />
            )}
            {canEdit ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2" data-testid="project-stage-dropdown">
                    <StageBadge stage={project.project_stage} stageConfig={stageConfig} />
                    <ChevronDown className="w-3 h-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {stages.map(s => (
                    <DropdownMenuItem key={s.slug} onClick={() => handleStageChange(s.slug)} className={project.project_stage === s.slug ? 'font-semibold' : ''}>
                      <StageBadge stage={s.slug} stageConfig={stageConfig} />
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <StageBadge stage={project.project_stage} stageConfig={stageConfig} />
            )}
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b mb-6 -mx-1">
          <nav className="flex gap-0 overflow-x-auto px-1" data-testid="project-tabs">
            {TABS.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  data-testid={`project-tab-${tab.id}`}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    isActive
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/30'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Custom Fields — shown above tab content */}
        {activeTab === 'overview' && (
          <div className="mb-6">
            <JobCustomFields jobId={id} canEdit={canEdit} />
          </div>
        )}

        {/* Tab Content */}
        <div data-testid={`project-tab-content-${activeTab}`}>
          {activeTab === 'overview' && (
            <ProjectOverview project={project} onRefresh={fetchProject} teamUsers={teamUsers} />
          )}
          {activeTab === 'budget' && (
            <ProjectBudget projectId={id} canEdit={canEdit} />
          )}
          {activeTab === 'schedule' && (
            <ProjectSchedule projectId={id} canEdit={canEdit} teamUsers={teamUsers} />
          )}
          {activeTab === 'tasks' && (
            <ProjectTasks projectId={id} canEdit={canEdit} teamUsers={teamUsers} />
          )}
          {activeTab === 'daily-logs' && (
            <ProjectDailyLogs projectId={id} canEdit={canEdit} teamUsers={teamUsers} />
          )}
          {activeTab === 'files' && (
            <ProjectFiles projectId={id} canEdit={canEdit} />
          )}
          {activeTab === 'activity' && (
            <ProjectActivity projectId={id} />
          )}
        </div>
      </div>
    </Layout>
  );
}
